import { ImperativeRouter, useRouter } from 'expo-router';
import UrlDocumentContext, {
  type UrlDocumentContextType,
} from 'features/urlDocumentScreens/UrlDocumentContext/UrlDocumentContext';
import { useCallback, useContext } from 'react';
import { Linking } from 'react-native';
import { isWeb } from 'shared/constants/platform';

export const createHtmlObjectUrl = (html: string) => {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  return URL.createObjectURL(blob);
};

export const navigateToPdfDocumentScreen = (
  url: string,
  router: ImperativeRouter,
) => {
  if (isWeb) {
    void Linking.openURL(url);
    return;
  }

  router.push('/document/pdf');
};

export const navigateToWebUrlScreen = (
  url: string,
  router: ImperativeRouter,
) => {
  if (isWeb) {
    void Linking.openURL(url);
    return;
  }

  router.push('/document/url');
};

export const navigateToHtmlDocumentScreen = (
  html: string,
  router: ImperativeRouter,
) => {
  if (isWeb) {
    // Full HTML documents are too large and brittle as data URLs, so web opens a temporary Blob URL instead.
    window.open(createHtmlObjectUrl(html), '_blank');
    return;
  }

  router.push('/document/html');
};

export const useOpenUrlDocumentScreen = () => {
  const { setDocumentHtml, setDocumentUrl }: UrlDocumentContextType =
    useContext(UrlDocumentContext);
  const router = useRouter();

  const openPdfDocumentScreen = useCallback(
    (url: string) => {
      setDocumentUrl?.(url);
      navigateToPdfDocumentScreen(url, router);
    },
    [setDocumentUrl, router],
  );

  const openWebUrlScreen = useCallback(
    (url: string) => {
      setDocumentUrl?.(url);
      navigateToWebUrlScreen(url, router);
    },
    [setDocumentUrl, router],
  );

  const openHtmlDocumentScreen = useCallback(
    (html: string) => {
      setDocumentHtml?.(html);
      navigateToHtmlDocumentScreen(html, router);
    },
    [setDocumentHtml, router],
  );

  return { openHtmlDocumentScreen, openPdfDocumentScreen, openWebUrlScreen };
};
